import React, { useState, useEffect } from 'react';
import { ShieldCheck, Lock, AlertTriangle, FileText, Hash, Clock, ListChecks, Loader2, Mail } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { facturasAPI, verifactuAPI } from '../../api';
import { toast } from '../../hooks/use-toast';
import FiscalStateBadge from './FiscalStateBadge';

const formatDateTime = (s) => {
  if (!s) return '-';
  try {
    return new Date(s).toLocaleString('es-ES', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch (_) { return s; }
};

const truncateHash = (h) => (h && h.length > 16 ? `${h.slice(0, 8)}…${h.slice(-8)}` : h || '');

/**
 * Panel fiscal completo de una factura: estado, registro fiscal (huella),
 * QR (si aplica), botones de emisión/anulación y modal de auditoría.
 */
const VerifactuPanel = ({ factura, onChanged }) => {
  const [stateData, setStateData] = useState(null);
  const [qrData, setQrData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [auditOpen, setAuditOpen] = useState(false);
  const [audit, setAudit] = useState([]);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelMotivo, setCancelMotivo] = useState('');
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailDestinatario, setEmailDestinatario] = useState('');

  const loadAll = async () => {
    if (!factura?.id) return;
    setLoading(true);
    try {
      const [stRes, qrRes] = await Promise.all([
        verifactuAPI.getState(factura.id),
        verifactuAPI.qrPreview(factura.id).catch(() => ({ data: null })),
      ]);
      setStateData(stRes.data);
      setQrData(qrRes.data);
    } catch (e) {
      // Silencioso: el panel no debe romper el detalle si falla
      setStateData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line
  }, [factura?.id]);

  const handleEmitir = async () => {
    if (!window.confirm(
      '¿Emitir fiscalmente esta factura?\n\n' +
      'Si el facturador tiene Verifactu activado, se generará un registro fiscal ' +
      'INMUTABLE encadenado (no podrá modificarse después).'
    )) return;
    setBusy(true);
    try {
      const { data } = await facturasAPI.emitirFiscal(factura.id);
      if (data.already_emitted) {
        toast({ title: 'La factura ya estaba emitida' });
      } else if (data.verifactu_activo) {
        toast({ title: 'Factura emitida (Verifactu)', description: `Registro #${data.fiscal_record_id}` });
      } else {
        toast({ title: 'Factura emitida (modo legacy)' });
      }
      await loadAll();
      onChanged && onChanged();
    } catch (e) {
      toast({
        title: 'Error al emitir',
        description: e?.response?.data?.detail || e.message,
        variant: 'destructive',
      });
    } finally {
      setBusy(false);
    }
  };

  const handleAnular = async () => {
    setBusy(true);
    try {
      await facturasAPI.anularFiscal(factura.id, cancelMotivo);
      toast({ title: 'Factura anulada fiscalmente' });
      setCancelOpen(false);
      setCancelMotivo('');
      await loadAll();
      onChanged && onChanged();
    } catch (e) {
      toast({
        title: 'Error al anular',
        description: e?.response?.data?.detail || e.message,
        variant: 'destructive',
      });
    } finally {
      setBusy(false);
    }
  };

  const openAudit = async () => {
    setAuditOpen(true);
    try {
      const { data } = await verifactuAPI.getAudit(factura.id);
      setAudit(Array.isArray(data) ? data : []);
    } catch (_) { setAudit([]); }
  };

  const handleEnviarEmail = async () => {
    setBusy(true);
    try {
      const { data } = await facturasAPI.enviarEmailCliente(factura.id, emailDestinatario || null);
      toast({
        title: 'Email enviado al cliente',
        description: `Destinatario: ${data.to || emailDestinatario}`,
      });
      setEmailOpen(false);
      setEmailDestinatario('');
    } catch (e) {
      toast({
        title: 'Error al enviar email',
        description: e?.response?.data?.detail || e.message,
        variant: 'destructive',
      });
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <Card><CardContent className="p-4 flex items-center text-gray-500">
        <Loader2 className="w-4 h-4 animate-spin mr-2" /> Cargando estado fiscal…
      </CardContent></Card>
    );
  }

  const fs = stateData;
  const isEmitida = fs?.estado_fiscal === 'emitida' || fs?.estado_fiscal === 'enviada';
  const isAnulada = fs?.estado_fiscal === 'anulada';
  const canEmit = !fs || fs?.estado_fiscal === 'borrador';
  const canCancel = isEmitida;
  const isVerifactuMode = fs?.modo === 'VERIFACTU' && fs?.fiscal_record;

  return (
    <Card className="border-2" data-testid="verifactu-panel">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center justify-between text-base">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-emerald-600" />
            Estado fiscal · Verifactu / SIF
          </div>
          <FiscalStateBadge fiscalState={fs} />
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Acciones */}
        <div className="flex flex-wrap gap-2">
          {canEmit && (
            <Button
              onClick={handleEmitir}
              disabled={busy}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="emitir-fiscal-btn"
            >
              <Lock className="w-4 h-4 mr-2" />
              Emitir fiscalmente
            </Button>
          )}
          {canCancel && (
            <Button
              variant="outline"
              onClick={() => setCancelOpen(true)}
              disabled={busy}
              className="border-red-300 text-red-700 hover:bg-red-50"
              data-testid="anular-fiscal-btn"
            >
              <AlertTriangle className="w-4 h-4 mr-2" />
              Anular fiscalmente
            </Button>
          )}
          <Button variant="ghost" onClick={openAudit} data-testid="auditoria-fiscal-btn">
            <ListChecks className="w-4 h-4 mr-2" /> Ver auditoría
          </Button>
          {(isEmitida || isAnulada) && (
            <Button
              variant="outline"
              onClick={() => setEmailOpen(true)}
              disabled={busy}
              className="border-blue-300 text-blue-700 hover:bg-blue-50"
              data-testid="enviar-email-cliente-btn"
            >
              <Mail className="w-4 h-4 mr-2" /> Enviar al cliente por email
            </Button>
          )}
        </div>

        {/* Info del estado */}
        {fs && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            {fs.emitida_at && (
              <div className="flex items-start gap-2">
                <Clock className="w-4 h-4 mt-0.5 text-gray-400" />
                <div>
                  <div className="text-gray-500 text-xs">Emitida</div>
                  <div>{formatDateTime(fs.emitida_at)}</div>
                </div>
              </div>
            )}
            {fs.modo && (
              <div className="flex items-start gap-2">
                <ShieldCheck className="w-4 h-4 mt-0.5 text-gray-400" />
                <div>
                  <div className="text-gray-500 text-xs">Modo</div>
                  <Badge variant="outline">{fs.modo}</Badge>
                </div>
              </div>
            )}
            {fs.legacy && (
              <div className="md:col-span-2 text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 text-xs">
                Modo <strong>Legacy</strong>: el facturador no tiene Verifactu activado.
                La factura ha sido congelada pero <strong>no</strong> se generó registro fiscal encadenado.
              </div>
            )}
            {fs.fiscal_record && (
              <>
                <div className="flex items-start gap-2">
                  <FileText className="w-4 h-4 mt-0.5 text-gray-400" />
                  <div>
                    <div className="text-gray-500 text-xs">Tipo registro</div>
                    <Badge>{fs.fiscal_record.tipo}</Badge>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Hash className="w-4 h-4 mt-0.5 text-gray-400" />
                  <div className="overflow-hidden">
                    <div className="text-gray-500 text-xs">Nº secuencia / Huella</div>
                    <div className="font-mono text-xs">
                      #{fs.fiscal_record.num_secuencia} ·{' '}
                      <span title={fs.fiscal_record.huella}>{truncateHash(fs.fiscal_record.huella)}</span>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* QR (sólo si Verifactu activo) */}
        {isVerifactuMode && qrData?.qr_disponible && (
          <div className="border rounded p-3 bg-gray-50 flex items-start gap-3">
            <img
              src={`https://api.qrserver.com/v1/create-qr-code/?size=140x140&data=${encodeURIComponent(qrData.url)}`}
              alt="QR Verifactu"
              className="w-32 h-32 border bg-white"
              data-testid="verifactu-qr-img"
            />
            <div className="text-xs space-y-1">
              <div className="font-semibold text-gray-700">QR Verifactu ({qrData.modo || 'TEST'})</div>
              <div className="text-gray-500 break-all">{qrData.url}</div>
              <div className="text-gray-400">
                El cliente puede verificar la autenticidad escaneando este QR en la web de la AEAT.
              </div>
            </div>
          </div>
        )}
      </CardContent>

      {/* Modal envío email al cliente */}
      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Enviar factura por email al cliente</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-sm bg-blue-50 border border-blue-200 rounded p-2 text-blue-700">
              Se enviará el PDF de la factura como adjunto.
              {isVerifactuMode && (
                <> El PDF incluirá el <strong>QR Verifactu</strong> para que el
                cliente pueda verificar la autenticidad en la sede de la AEAT.</>
              )}
            </div>
            <div>
              <Label className="text-xs">
                Destinatario (vacío = email del cliente)
              </Label>
              <Input
                type="email"
                value={emailDestinatario}
                onChange={(e) => setEmailDestinatario(e.target.value)}
                placeholder="cliente@ejemplo.com"
                data-testid="email-destinatario-input"
              />
              <p className="text-xs text-gray-500 mt-1">
                Si lo dejas vacío, se enviará al email guardado en la ficha del
                cliente.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailOpen(false)} disabled={busy}>
              Cancelar
            </Button>
            <Button
              onClick={handleEnviarEmail}
              disabled={busy}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="confirmar-enviar-email-btn"
            >
              {busy && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              <Mail className="w-4 h-4 mr-2" /> Enviar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal anulación */}
      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Anular factura fiscalmente</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-sm bg-red-50 border border-red-200 rounded p-2 text-red-700">
              Esta acción es <strong>irreversible</strong>. Se generará un registro
              ANULACION encadenado al ledger fiscal.
            </div>
            <div>
              <Label className="text-xs">Motivo (opcional)</Label>
              <Textarea
                value={cancelMotivo}
                onChange={(e) => setCancelMotivo(e.target.value)}
                placeholder="Ej: error en concepto, datos cliente incorrectos…"
                rows={3}
                data-testid="anular-motivo-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelOpen(false)} disabled={busy}>
              Cancelar
            </Button>
            <Button
              onClick={handleAnular}
              disabled={busy}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirmar-anular-btn"
            >
              {busy && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Anular factura
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal auditoría */}
      <Dialog open={auditOpen} onOpenChange={setAuditOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Auditoría fiscal · {factura?.numero_factura}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-[60vh] overflow-auto">
            {audit.length === 0 ? (
              <p className="text-sm text-gray-500 text-center p-6">
                No hay registros de auditoría para esta factura.
              </p>
            ) : (
              audit.map((entry) => (
                <div key={entry.id} className="border rounded p-3 text-sm">
                  <div className="flex items-center justify-between">
                    <Badge variant="outline" className="font-mono">{entry.accion}</Badge>
                    <span className="text-xs text-gray-500">{formatDateTime(entry.ts)}</span>
                  </div>
                  {entry.detalles && Object.keys(entry.detalles).length > 0 && (
                    <pre className="mt-2 text-xs bg-gray-50 p-2 rounded overflow-x-auto">
                      {JSON.stringify(entry.detalles, null, 2)}
                    </pre>
                  )}
                  {(entry.user_id || entry.ip) && (
                    <div className="text-xs text-gray-400 mt-1">
                      {entry.user_id && `usuario #${entry.user_id}`}{entry.user_id && entry.ip && ' · '}{entry.ip}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAuditOpen(false)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default VerifactuPanel;
